import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { PartTypeLiveSearchItem } from '../../../../../@types/partType.type';
import { useGetPartTypesByClientContract } from '../../../../../queries/part-type-query';

export interface ExceptionalPartData {
  serial: string;
  partTypeId: number;
  partTypeName: string;
  categoryCode: string;
  deviceImage: File | null;
  serialImage: File | null;
}

export interface ExceptionalPartFormProps {
  clientId: number;
  contractId: number;
  onDataChange: (data: ExceptionalPartData) => void;
}

export const ExceptionalPartForm: React.FC<ExceptionalPartFormProps> = ({
  clientId,
  contractId,
  onDataChange,
}) => {
  const partTypesQuery = useGetPartTypesByClientContract(clientId, contractId);
  const [serial, setSerial] = useState('');
  const [selectedPartType, setSelectedPartType] = useState<PartTypeLiveSearchItem | null>(null);
  const [filterText, setFilterText] = useState('');
  const [deviceImage, setDeviceImage] = useState<File | null>(null);
  const [serialImage, setSerialImage] = useState<File | null>(null);

  useEffect(() => {
    partTypesQuery.refetch();
  }, [clientId, contractId]);

  // Filter part types in browser rather than re-querying DB
  const filteredPartTypes = useMemo(() => {
    const all = partTypesQuery.data ?? [];
    if (!filterText) return all;
    const lower = filterText.toLowerCase();
    return all.filter((pt) => pt.name.toLowerCase().includes(lower) || pt.number?.toLowerCase().includes(lower));
  }, [partTypesQuery.data, filterText]);

  // Notify parent of changes
  useEffect(() => {
    onDataChange({
      serial,
      partTypeId: selectedPartType?.id ?? 0,
      partTypeName: selectedPartType?.name ?? '',
      categoryCode: selectedPartType?.categoryCode ?? '',
      deviceImage,
      serialImage,
    });
  }, [serial, selectedPartType, deviceImage, serialImage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1.5, border: '1px dashed', borderColor: 'v4.border', borderRadius: 1 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Exceptional Part Creation</Typography>

      <TextField
        size="small" label="Serial Number" value={serial}
        onChange={(e) => setSerial(e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
      />

      <Autocomplete
        size="small"
        options={filteredPartTypes}
        getOptionLabel={(opt) => opt.name}
        inputValue={filterText}
        onInputChange={(_, val) => setFilterText(val)}
        onChange={(_, val) => setSelectedPartType(val)}
        loading={partTypesQuery.isFetching}
        renderInput={(params) => (
          <TextField {...params} label="Part Type"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{option.name}</Typography>
              {option.number && <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>{option.number}</Typography>}
            </Box>
          </li>
        )}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            Photo of whole device
          </Typography>
          <Button component="label" variant="outlined" size="small" fullWidth
            sx={{ textTransform: 'none', fontSize: '11px', justifyContent: 'flex-start' }}>
            {deviceImage ? deviceImage.name : 'Choose file...'}
            <input type="file" accept="image/*" hidden onChange={(e) => setDeviceImage(e.target.files?.[0] ?? null)} />
          </Button>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            Photo of serial number label
          </Typography>
          <Button component="label" variant="outlined" size="small" fullWidth
            sx={{ textTransform: 'none', fontSize: '11px', justifyContent: 'flex-start' }}>
            {serialImage ? serialImage.name : 'Choose file...'}
            <input type="file" accept="image/*" hidden onChange={(e) => setSerialImage(e.target.files?.[0] ?? null)} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
